import * as React from "react"
import webify from "../util/webify"
import { post } from "@util/api"
import { observer } from "mobx-react"
import SVG from "react-inlinesvg"

import { Person, PersonProps } from "../models/Person"
import ParticipationList from "../lists/ParticipationList"
import { useState } from "react"
import userSvg from "../../../priv/static/svg/generic/user.svg"
import plusSvg from "../../../priv/static/svg/generic/plus-circle.svg"
import Participation, { ParticipationProps } from "../models/Participation"
import PersonList from "../lists/PersonList"

const registerNewPassenger = async (passengerPartyId: number, person: Person) => {
  return await post("/api/v1/participations", {
    passenger_party_id: passengerPartyId,
    person: {
      first_name: person.firstName,
      middle_name: person.middleName,
      last_name: person.lastName,
    },
  })
}

const registerExistingPassenger = async (passengerPartyId: number, person: Person) => {
  return await post("/api/v1/participations", {
    passenger_party_id: passengerPartyId,
    person_id: person.id,
  })
}

interface NewPassengerEditorProps {
  person: Person
  onSubmit: () => any
}

// autocomplete=nope is here because Chrome is too cool for standards
const NewPassengerEditor = observer((props: NewPassengerEditorProps) => {
  const { person, onSubmit } = props
  const firstNameInputRef: any = React.createRef()
  return (
    <form
      className="items-end form-grid-setup lg:grid-cols-4"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onSubmit()
        firstNameInputRef.current.focus()
      }}
    >
      <div>
        <label className="font-bold text-gray-700 gc-uppercase" htmlFor="first_name">
          Passenger First Name
        </label>
        <span className="gc-uppercase">*</span>
        <input
          type="text"
          name="first_name"
          id="first_name"
          value={person.firstName}
          ref={firstNameInputRef}
          autoComplete="nope"
          autoFocus
          required
          onChange={(e) => (person.firstName = e.target.value)}
        />
      </div>
      <div>
        <label className="font-bold text-gray-700 gc-uppercase" htmlFor="middle_name">
          Passenger Middle Name
        </label>
        <input
          type="text"
          name="middle_name"
          id="middle_name"
          value={person.middleName}
          autoComplete="nope"
          onChange={(e) => (person.middleName = e.target.value)}
        />
      </div>
      <div>
        <label className="font-bold text-gray-700 gc-uppercase" htmlFor="last_name">
          Passenger Last Name
        </label>
        <span className="gc-uppercase">*</span>
        <input
          type="text"
          name="last_name"
          id="last_name"
          value={person.lastName}
          autoComplete="nope"
          required
          onChange={(e) => (person.lastName = e.target.value)}
        />
      </div>
      <div className="grid lg:block">
        <button className="flex space-x-1 button less-important" type="submit">
          <SVG src={userSvg} className="inline" height={16} width={16} />
          <span>Add Passenger</span>
        </button>
      </div>
    </form>
  )
})

interface ExistingPassengerListProps {
  className?: string
  participations: ParticipationList
}

let ExistingPassengerList = observer(
  ({ participations, className }: ExistingPassengerListProps) =>
    participations.items.length > 0 && (
      <div className="" data-role="existing-passenger-list">
        <h2 className="mb-0">Current Passengers</h2>
        <div className="italic text-gray-700">
          Passenger(s) you've created for this trip, you're responsible for paying for these passengers.
        </div>
        <div className="my-2">
          {participations.items.map((participation, idx) => (
            <div key={idx} className="flex items-center p-1 space-x-2 passenger-added" data-role="person">
              <ActiveOrCancelledDot cancelled={participation.cancelled} />
              <span>{participation.person.fullName}</span>
              <CancelledTag cancelled={participation.cancelled} />
            </div>
          ))}
        </div>
      </div>
    )
)

let ActiveOrCancelledDot = ({ cancelled }: { cancelled: boolean }) => {
  if (cancelled) {
    return <div className="bg-red-700 dot"></div>
  } else {
    return <div className="bg-green-700 dot"></div>
  }
}

let CancelledTag = ({ cancelled }: { cancelled: boolean }) => {
  if (!cancelled) return null

  return <span className="bg-gray-200 px-1 text-sm text-gray-700 italic">Canceled Passenger</span>
}

interface OtherPassengerListProps {
  className?: string
  passengerPartyId: number
  participations: ParticipationList
  otherPersons: PersonList
}
const OtherPassengerList = observer((props: OtherPassengerListProps) => {
  const { passengerPartyId, participations, otherPersons, className } = props
  return (
    otherPersons.items.length > 0 && (
      <div className={className}>
        {otherPersons.items.length > 0 && (
          <>
            <h2>Previous Passengers</h2>

            <div className="mt-1 italic text-gray-700">
              These are passengers you've created for other trips, who are not traveling on this particular trip.
            </div>
          </>
        )}
        {otherPersons.items.map((person, idx) => (
          <div key={idx}>
            <div className="grid items-center grid-cols-2 my-2">
              <div className="grid items-center justify-start grid-flow-col">
                <div className="bg-red-600 dot"></div>
                <span>{person.fullName}</span>
              </div>
              <div className="grid justify-end grid-flow-col">
                <a
                  className="grid items-center justify-start grid-flow-col gap-1 border-gray-200 button secondary hover:no-underline"
                  data-role={`add-to-trip[${person.id}]`}
                  onClick={() => {
                    registerExistingPassenger(passengerPartyId, person)
                    participations.addCopyOfPerson(person)
                    otherPersons.remove(person)
                  }}
                >
                  <SVG src={plusSvg} height={16} className="stroke-2" /> Add to Trip
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  )
})

interface PassengerPartyEditorProps {
  proceedPath: string
  partyParticipations: ParticipationProps[]
  otherPersons: PersonProps[]
  passengerPartyId: number
}
const PassengerPartyEditor = observer((props: PassengerPartyEditorProps) => {
  const { passengerPartyId, proceedPath } = props
  const [participations] = useState(new ParticipationList(props.partyParticipations))
  const [otherPersons] = useState(new PersonList(props.otherPersons))

  const [newParticipation] = useState(new Participation({ person: {} }))

  return (
    <div className="form-grid-setup">
      <div className="form-grid-setup lg:grid-cols-2">
        <ExistingPassengerList participations={participations} />
        <OtherPassengerList
          passengerPartyId={passengerPartyId}
          participations={participations}
          otherPersons={otherPersons}
        />
      </div>

      {participations.items.length > 0 && (
        <div className="-mt-4">
          <div className="italic text-gray-700">
            If you are finished adding your passengers on this trip, click the button below.
          </div>

          <div className="button-row">
            <a href={proceedPath} className="button">
              Proceed to Next Step
            </a>
          </div>
        </div>
      )}

      <div className="p-6 border-gray-200 form-grid-setup">
        <div>
          {participations.items.length > 0 ? (
            <h2 className="mb-1">Add Another Passenger</h2>
          ) : (
            <h2 className="mb-1">Add Passenger</h2>
          )}
          <h3 className="font-normal">Only add the name of people who intend to travel on this trip.</h3>
        </div>
        <NewPassengerEditor
          person={newParticipation.person}
          onSubmit={() => {
            registerNewPassenger(passengerPartyId, newParticipation.person)
            participations.addCopyOfPerson(newParticipation.person)
            newParticipation.person.reset()
          }}
        />
      </div>
    </div>
  )
})

webify("passenger-party-editor", PassengerPartyEditor)
